import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Autocomplete,
  Chip,
  Alert,
  CircularProgress,
} from '@mui/material';
import { GruppoProgetti, CreateGruppoProgettiDto, UpdateGruppoProgettiDto } from '../types/gruppo';
import { Progetto } from '../types';
import * as api from '../services/api';

interface GruppoProgettiDialogProps {
  open: boolean;
  gruppo: GruppoProgetti | null;
  onClose: (saved: boolean) => void;
}

const GruppoProgettiDialog: React.FC<GruppoProgettiDialogProps> = ({ open, gruppo, onClose }) => {
  const [nome, setNome] = useState('');
  const [descrizione, setDescrizione] = useState('');
  const [colore, setColore] = useState('');
  const [selectedProgetti, setSelectedProgetti] = useState<Progetto[]>([]);
  const [availableProgetti, setAvailableProgetti] = useState<Progetto[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadingProgetti, setLoadingProgetti] = useState(false);
  const [searchInput, setSearchInput] = useState('');

  // Ricerca server-side con debounce 300ms
  const searchProgetti = useCallback(async (term: string) => {
    setLoadingProgetti(true);
    try {
      const response = await api.getProgetti(false, 1, 100, term);
      setAvailableProgetti(response.data || []);
    } catch (err: any) {
      console.error('Errore caricamento progetti:', err);
    } finally {
      setLoadingProgetti(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    const timer = setTimeout(() => searchProgetti(searchInput), 300);
    return () => clearTimeout(timer);
  }, [searchInput, open, searchProgetti]);

  useEffect(() => {
    if (open) {
      setSearchInput('');
      if (gruppo) {
        setNome(gruppo.nome);
        setDescrizione(gruppo.descrizione || '');
        setColore(gruppo.colore || '');
        setSelectedProgetti(gruppo.progetti);
      } else {
        setNome('');
        setDescrizione('');
        setColore('');
        setSelectedProgetti([]);
      }
      setError(null);
    }
  }, [open, gruppo]);

  const handleSave = async () => {
    if (!nome.trim()) {
      setError('Il nome del gruppo è obbligatorio');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      let gruppoId: number;

      if (gruppo) {
        // Update gruppo
        const updateDto: UpdateGruppoProgettiDto = {
          nome: nome.trim(),
          descrizione: descrizione.trim() || undefined,
          colore: colore.trim() || undefined,
        };

        const response = await api.updateGruppoProgetti(gruppo.id, updateDto);
        gruppoId = response.id;

        // Calcola progetti da aggiungere/rimuovere
        const currentProgettiIds = gruppo.progetti.map(p => p.id);
        const newProgettiIds = selectedProgetti.map(p => p.id);

        const toAdd = newProgettiIds.filter(id => !currentProgettiIds.includes(id));
        const toRemove = currentProgettiIds.filter(id => !newProgettiIds.includes(id));

        for (const progettoId of toAdd) {
          await api.addProgettoToGruppo(gruppoId, progettoId);
        }
        for (const progettoId of toRemove) {
          await api.removeProgettoFromGruppo(gruppoId, progettoId);
        }
      } else {
        // Create gruppo
        const createDto: CreateGruppoProgettiDto = {
          nome: nome.trim(),
          descrizione: descrizione.trim() || undefined,
          colore: colore.trim() || undefined,
        };

        const response = await api.createGruppoProgetti(createDto);
        gruppoId = response.id;

        for (const progetto of selectedProgetti) {
          await api.addProgettoToGruppo(gruppoId, progetto.id);
        }
      }

      onClose(true);
    } catch (err: any) {
      console.error('Errore salvataggio gruppo:', err);
      setError(err.response?.data?.error || 'Errore salvataggio gruppo');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    onClose(false);
  };

  // Opzioni: progetti trovati dalla ricerca + quelli già selezionati (per non sparire dalla lista)
  const options = [
    ...selectedProgetti,
    ...availableProgetti.filter(p => !selectedProgetti.some(sp => sp.id === p.id)),
  ];

  return (
    <Dialog open={open} onClose={handleCancel} maxWidth="md" fullWidth>
      <DialogTitle>{gruppo ? 'Modifica Gruppo' : 'Nuovo Gruppo'}</DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField
            label="Nome Gruppo"
            value={nome}
            onChange={(e) => setNome(e.target.value)}
            fullWidth
            required
            autoFocus
          />

          <TextField
            label="Descrizione"
            value={descrizione}
            onChange={(e) => setDescrizione(e.target.value)}
            fullWidth
            multiline
            rows={3}
          />

          <Box>
            <TextField
              label="Colore (hex)"
              value={colore}
              onChange={(e) => setColore(e.target.value)}
              fullWidth
              placeholder="#FF5733"
              helperText="Formato: #RRGGBB (opzionale)"
              InputProps={{
                endAdornment: colore && (
                  <Box
                    sx={{
                      width: 24,
                      height: 24,
                      bgcolor: colore,
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'divider',
                    }}
                  />
                ),
              }}
            />
          </Box>

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Progetti nel gruppo
            </Typography>
            <Autocomplete
              multiple
              options={options}
              value={selectedProgetti}
              onChange={(_, newValue) => setSelectedProgetti(newValue)}
              onInputChange={(_, newInputValue) => setSearchInput(newInputValue)}
              filterOptions={(x) => x}
              loading={loadingProgetti}
              getOptionLabel={(option) =>
                option.id_hrp ? `[${option.id_hrp}] ${option.titolo}` : option.titolo
              }
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Cerca e seleziona progetti..."
                  helperText="Digita per cercare, i progetti saranno ordinati alfabeticamente nel gruppo"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingProgetti ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    label={option.id_hrp ? `[${option.id_hrp}] ${option.titolo}` : option.titolo}
                    {...getTagProps({ index })}
                    size="small"
                  />
                ))
              }
            />
          </Box>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleCancel} disabled={loading}>
          Annulla
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={loading || !nome.trim()}
        >
          {loading ? <CircularProgress size={24} /> : 'Salva'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default GruppoProgettiDialog;
